import React, { useState, useEffect } from 'react';
import { groupBookingsService } from '../../../services';
import GroupForm from './GroupForm';
import GroupDetails from './GroupDetails';

// Voyager Pro reskin (Figma GroupsPage) - see memory entity "TPCRM Voyager
// Pro CRM Reskin Project". Shared by agency (/agent/groups) and independent
// (/independent/groups) agents - groupBookingsService.js normalizes the two
// different backends behind one shape, mirroring leadsService.js's pattern.

export const GROUP_TYPE_CFG = {
  destination_wedding: { label: 'Destination Wedding', color: '#DB2777', bg: 'rgba(219,39,119,0.08)', icon: '💍' },
  corporate_retreat: { label: 'Corporate Retreat', color: '#1A2B4A', bg: 'rgba(26,43,74,0.08)', icon: '🏢' },
  cruise_block: { label: 'Cruise Block', color: '#0284C7', bg: 'rgba(2,132,199,0.08)', icon: '🚢' },
  hotel_block: { label: 'Hotel Block', color: '#C4871F', bg: 'rgba(196,135,31,0.08)', icon: '🏨' },
  group_tour: { label: 'Group Tour', color: '#059669', bg: 'rgba(5,150,105,0.08)', icon: '🗺️' },
  family_reunion: { label: 'Family Reunion', color: '#7C3AED', bg: 'rgba(124,58,237,0.08)', icon: '👨‍👩‍👧‍👦' },
  sports_adventure: { label: 'Sports & Adventure', color: '#EA580C', bg: 'rgba(234,88,12,0.08)', icon: '🏔️' },
  educational: { label: 'Educational / School', color: '#0891B2', bg: 'rgba(8,145,178,0.08)', icon: '🎓' },
  all_inclusive_resort: { label: 'All-Inclusive Resort', color: '#0D9488', bg: 'rgba(13,148,136,0.08)', icon: '🌴' },
};

export const STATUS_CFG = {
  planning: { label: 'Planning', color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200', dot: 'bg-slate-400' },
  accepting: { label: 'Accepting', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
  confirmed: { label: 'Confirmed', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500' },
  traveling: { label: 'Traveling', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  completed: { label: 'Completed', color: 'text-slate-500', bg: 'bg-slate-100', border: 'border-slate-200', dot: 'bg-slate-400' },
  cancelled: { label: 'Cancelled', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-400' },
};

export const PAY_STATUS_CFG = {
  unpaid: { label: 'Unpaid', color: 'text-red-600', bg: 'bg-red-50' },
  deposit_paid: { label: 'Deposit Paid', color: 'text-amber-700', bg: 'bg-amber-50' },
  partial: { label: 'Partial', color: 'text-orange-600', bg: 'bg-orange-50' },
  paid_in_full: { label: 'Paid in Full', color: 'text-emerald-700', bg: 'bg-emerald-50' },
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
const fmtCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const GroupsManagement = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { fetchGroups(); }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const data = await groupBookingsService.getAll();
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupCreate = async (groupData) => {
    try {
      await groupBookingsService.create(groupData);
      await fetchGroups();
      setShowForm(false);
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Failed to create group booking. Please try again.');
    }
  };

  const safeGroups = Array.isArray(groups) ? groups : [];
  const filteredGroups = safeGroups.filter(group => {
    if (filter !== 'all' && group.status !== filter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!group.title?.toLowerCase().includes(search) && !group.destination?.toLowerCase().includes(search)) return false;
    }
    return true;
  });

  const stats = {
    total: safeGroups.length,
    accepting: safeGroups.filter(g => g.status === 'accepting').length,
    confirmed: safeGroups.filter(g => g.status === 'confirmed').length,
    totalRevenue: safeGroups.reduce((sum, g) => sum + parseFloat(g.total_revenue || 0), 0),
  };

  return (
    <div className="voyager-app flex h-full min-h-0">
      <div className={`flex-1 overflow-y-auto min-w-0 ${selectedGroup ? 'hidden lg:block' : ''}`}>
        <div className="max-w-5xl mx-auto p-6 space-y-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-1">Group Bookings</h1>
              <p className="text-[var(--muted-foreground)] text-sm">Manage group travel and track participant registrations.</p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold hover:brightness-105 transition-all shadow-sm"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Create Group
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Groups', value: stats.total, icon: '👥' },
              { label: 'Accepting', value: stats.accepting, icon: '📝' },
              { label: 'Confirmed', value: stats.confirmed, icon: '✅' },
              { label: 'Total Revenue', value: fmtCurrency(stats.totalRevenue), icon: '💰' },
            ].map(s => (
              <div key={s.label} className="bg-white border border-[var(--border)] rounded-xl p-4">
                <div className="text-xl mb-1">{s.icon}</div>
                <div className="text-2xl font-bold text-[var(--primary)]">{s.value}</div>
                <div className="text-xs text-[var(--muted-foreground)] mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input
                type="text"
                placeholder="Search by name or destination…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors"
              />
            </div>
            <select value={filter} onChange={e => setFilter(e.target.value)} className="px-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none cursor-pointer flex-shrink-0">
              <option value="all">All Statuses</option>
              {Object.keys(STATUS_CFG).map(s => <option key={s} value={s}>{STATUS_CFG[s].label}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="text-center py-16 text-[var(--muted-foreground)] text-sm">Loading group bookings…</div>
          ) : filteredGroups.length === 0 ? (
            <div className="text-center py-16 bg-white border-2 border-dashed border-[var(--border)] rounded-2xl">
              <div className="text-4xl mb-3">👥</div>
              <div className="text-sm font-medium text-[var(--foreground)] mb-1">No group bookings yet</div>
              <div className="text-xs text-[var(--muted-foreground)] mb-4">Create a group booking to start tracking participants and payments.</div>
              <button onClick={() => setShowForm(true)} className="px-5 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold hover:brightness-105 transition-all">Create Group Booking</button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredGroups.map(group => {
                const typeCfg = GROUP_TYPE_CFG[group.component_type] || { label: group.component_type, color: '#6B7A94', bg: 'rgba(107,122,148,0.08)', icon: '📋' };
                const statusCfg = STATUS_CFG[group.status] || STATUS_CFG.planning;
                return (
                  <button
                    key={group.id}
                    onClick={() => setSelectedGroup(group)}
                    className="w-full text-left bg-white border border-[var(--border)] rounded-xl p-4 hover:border-[var(--accent)] transition-colors flex items-center gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm truncate text-[var(--foreground)]">{group.title}</span>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${statusCfg.bg} ${statusCfg.color} ${statusCfg.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
                          {statusCfg.label}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ backgroundColor: typeCfg.bg, color: typeCfg.color }}>
                          {typeCfg.icon} {typeCfg.label}
                        </span>
                      </div>
                      <div className="text-xs text-[var(--muted-foreground)] mt-1 truncate">
                        {group.destination} · {fmtDate(group.start_date)} – {fmtDate(group.end_date)}
                      </div>
                    </div>
                    <div className="hidden sm:block text-xs text-[var(--muted-foreground)] flex-shrink-0 text-right">
                      {group.participant_count || group.current_participants || 0}{group.max_participants ? `/${group.max_participants}` : ''} joined
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {selectedGroup && (
        <GroupDetails group={selectedGroup} onClose={() => setSelectedGroup(null)} onUpdate={fetchGroups} />
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowForm(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '92vh' }}>
            <GroupForm onClose={() => setShowForm(false)} onSubmit={handleGroupCreate} />
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupsManagement;
